import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import './styles/chatAnimations.css';
import remarkGfm from 'remark-gfm';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PdfPreview from './PdfPreview';

function ChatHistory({
  chatHistory,
  playingAudioId,
  isPlaying,
  pauseAudio,
  playAudioFromUrl,
  playAudioFromBase64,
  seekAudio,
  stopAudio,
  isLoading,
  currentThinkingState,
  messagesEndRef,
  onRestartCurrentAudio,
}) {
  const [previewPdf, setPreviewPdf] = useState({show: false, url: ''});
  // Track expanded state for each chat's accordions
  const [expandedAccordions, setExpandedAccordions] = useState({});
  
  const toggleAccordion = (chatId, section) => {
    const key = `${chatId}-${section}`;
    setExpandedAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  const isExpanded = (chatId, section) => {
    return expandedAccordions[`${chatId}-${section}`] || false;
  };
  
  const normalizeUrl = (src) => {
    const raw = (src?.gcs_url || '').replace(/`/g, '').trim();
    if (/\.pdf(\?|#|$)/i.test(raw)) {
      try {
        const u = new URL(raw);
        return u.href;
      } catch {
        return raw;
      }
    }
    const base = 'https://storage.googleapis.com/curebay-staging-intellimed-documents/';
    const file = src?.file ? encodeURIComponent(src.file) : '';
    return file ? `${base}${file}` : raw;
  };
  const onSourceClick = (src) => {
    setPreviewPdf({show: true, url: normalizeUrl(src)});
  };
  if (!chatHistory || chatHistory.length === 0) return null;
  return (
    <div className="mt-8 space-y-5">
      {chatHistory.map((chat) => {
        const timestamp = chat?.timestamp
          ? new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : '';
        return (
          <div key={chat.id} className="flex flex-col message-enter">
            {chat.type === 'user' ? (
              <div className="flex justify-end items-end gap-2">
                <span className="text-xs text-gray-400 mb-2 font-medium">{timestamp}</span>
                <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 text-white px-5 py-3.5 rounded-3xl max-w-md shadow-lg hover:shadow-xl transition-shadow duration-200">
                  {chat.isAudio ? (
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (playingAudioId === chat.id && isPlaying) {
                            pauseAudio();
                          } else if (chat.audioUrl) {
                            stopAudio && stopAudio();
                            playAudioFromUrl(chat.audioUrl, chat.id);
                          }
                        }}
                        className="flex items-center justify-center w-9 h-9 flex-shrink-0 hover:scale-105 active:scale-95 transition-transform"
                        aria-label={playingAudioId === chat.id && isPlaying ? 'Pause audio' : 'Play audio'}
                      >
                        {playingAudioId === chat.id && isPlaying ? (
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                      <div className="flex items-center gap-1 flex-1 h-8">
                        {[4, 6, 8, 5, 9, 7, 10, 6, 11, 7, 9, 5, 10, 6, 8, 7, 9, 6, 8, 5, 7, 6, 8, 5, 6].map((height, i) => (
                          <div key={`${chat.id}-${i}`} className="w-0.5 rounded-full bg-white/80" style={{ height: `${height * 2}px` }} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]} className="text-sm leading-relaxed prose prose-blue max-w-none">
                      {chat.message}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex justify-start items-end gap-2">
                <div className="bg-white text-gray-800 px-5 py-3.5 rounded-3xl max-w-md border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200">
                  {chat.audio && chat.audio.length > 100 ? (
                    <div className="flex justify-end mb-3">
                      {playingAudioId === chat.id ? (
                        <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
                          <Button
                            icon="pi pi-replay"
                            className="p-button-text p-button-sm text-gray-600 hover:bg-gray-100 w-7 h-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRestartCurrentAudio && onRestartCurrentAudio();
                            }}
                            aria-label="Restart"
                          />
                          <Button
                            icon="pi pi-step-backward"
                            className="p-button-text p-button-sm text-gray-600 hover:bg-gray-100 w-7 h-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              seekAudio(-10);
                            }}
                            aria-label="Rewind 10 seconds"
                          />
                          <Button
                            icon={isPlaying ? 'pi pi-pause' : 'pi pi-play'}
                            className="p-button-rounded p-button-sm bg-gray-700 text-white hover:bg-gray-800 w-8 h-8 border-0 shadow-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isPlaying) {
                                pauseAudio();
                              } else if (chat.audio) {
                                stopAudio && stopAudio();
                                playAudioFromBase64(chat.audio, chat.id);
                              }
                            }}
                            aria-label={isPlaying ? 'Pause' : 'Resume'}
                          />
                          <Button
                            icon="pi pi-step-forward"
                            className="p-button-text p-button-sm text-gray-600 hover:bg-gray-100 w-7 h-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              seekAudio(10);
                            }}
                            aria-label="Forward 10 seconds"
                          />
                          <Button
                            icon="pi pi-stop"
                            className="p-button-text p-button-sm text-red-600 hover:bg-red-50 w-7 h-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              stopAudio();
                            }}
                            aria-label="Stop"
                          />
                        </div>
                      ) : (
                        // <Button
                        //   icon="pi pi-volume-up"
                        //   label="PLAY"
                        //   className="p-button-outlined p-button-sm border-2 hover:bg-gray-50 font-semibold transition-all duration-200"
                        //   onClick={(e) => {
                        //     e.stopPropagation();
                        //     if (chat.audio && chat.audio.length > 100) {
                        //       stopAudio && stopAudio();
                        //       playAudioFromBase64(chat.audio, chat.id);
                        //     }
                        //   }}
                        //   aria-label="Play audio"
                        // />
                        ""
                      )}
                    </div>
                  ):null}
                  {chat.streaming && !(chat.answer || chat.message) ? (
                    <div className="space-y-2">
                      <div className="skeleton-line w-64"></div>
                      <div className="skeleton-line w-48"></div>
                      <div className="skeleton-line w-40"></div>
                    </div>
                  ) : (
                    <div className={chat.streaming ? 'streaming-blur' : ''}>
                      {(chat.answer || chat.message) && (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]} className="text-sm leading-relaxed prose max-w-none">
                          {chat.answer || chat.message}
                        </ReactMarkdown>
                      )}
                      {(Array.isArray(chat.used_sources) && chat.used_sources.length > 0) || (Array.isArray(chat.sources) && chat.sources.length > 0) ? (
                        <div className="mt-3 pt-2 border-t border-gray-200 space-y-2">
                          {Array.isArray(chat.used_sources) && chat.used_sources.length > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <button
                                onClick={() => toggleAccordion(chat.id, 'used')}
                                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-gray-700">Used resources</span>
                                  <span className="text-xs text-gray-500">({chat.used_sources.length})</span>
                                </div>
                                <svg
                                  className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded(chat.id, 'used') ? 'rotate-180' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {isExpanded(chat.id, 'used') && (
                                <div className="px-3 py-2 bg-white">
                                  <ol className="space-y-1">
                                    {(chat.used_sources || chat.sources).map((src, idx) => {
                                      const s = src || {};
                                      const score = typeof s?.score === 'number' ? s.score : typeof s?.max_score === 'number' ? s.max_score : undefined;
                                      const scorePct = typeof score === 'number' ? `${(score * 100).toFixed(1)}%` : '';
                                      return (
                                        <li key={`${chat.id}-used-${idx}`} className="flex items-start text-xs text-gray-700 hover:underline cursor-pointer" onClick={() => onSourceClick(s)}>
                                          <span className="mr-2">{idx + 1}.</span>
                                          <span className="text-blue-600 hover:text-blue-700">
                                            {s?.file || 'Unknown'}
                                            {typeof s?.pages !== 'undefined' ? ` • page[${s.pages}]` : ''}
                                            {scorePct && (
                                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                                {scorePct}
                                              </span>
                                            )}
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ol>
                                </div>
                              )}
                            </div>
                          )}
                          {(() => {
                            const all = Array.isArray(chat.sources) ? chat.sources : [];
                            const used = Array.isArray(chat.used_sources) ? chat.used_sources : [];
                            const key = (s) => `${s?.file || ''}|${Array.isArray(s?.pages) ? s.pages.join(',') : s?.pages ?? ''}`;
                            const usedKeys = new Set(used.map(key));
                            const additional = Array.isArray(chat.additional_sources) ? chat.additional_sources : all.filter((s) => !usedKeys.has(key(s)));

                            // Console logs for debugging used and additional resources
                            if (used.length || additional.length) {
                              // Keep logs compact but informative
                              console.log('📚 Resources for chat', chat.id, {
                                used,
                                additional,
                              });
                            }

                            // Show message when used resources exist (regardless of additional)
                            if (used.length > 0) {
                              return (
                                <>
                                  {additional.length > 0 && (
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                      <button
                                        onClick={() => toggleAccordion(chat.id, 'additional')}
                                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-semibold text-gray-700">Additional resources</span>
                                          <span className="text-xs text-gray-500">({additional.length})</span>
                                        </div>
                                        <svg
                                          className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded(chat.id, 'additional') ? 'rotate-180' : ''}`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                      {isExpanded(chat.id, 'additional') && (
                                        <div className="px-3 py-2 bg-white">
                                          <ol className="space-y-1">
                                            {additional.map((src, idx) => {
                                              const s = src || {};
                                              const score = typeof s?.score === 'number' ? s.score : typeof s?.max_score === 'number' ? s.max_score : undefined;
                                              const scorePct = typeof score === 'number' ? `${(score * 100).toFixed(1)}%` : '';
                                              return (
                                                <li key={`${chat.id}-add-${idx}`} className="flex items-start text-xs text-gray-700 hover:underline cursor-pointer" onClick={() => onSourceClick(s)}>
                                                  <span className="mr-2">{idx + 1}.</span>
                                                  <span className="text-blue-600 hover:text-blue-700">
                                                    {s?.file || 'Unknown'}
                                                    {typeof s?.pages !== 'undefined' ? ` • page[${s.pages}]` : ''}
                                                    {scorePct && (
                                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                                        {scorePct}
                                                      </span>
                                                    )}
                                                  </span>
                                                </li>
                                              );
                                            })}
                                          </ol>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <div className="mt-3 pt-2">
                                    <div className="bg-white rounded-lg px-4 py-2.5 shadow-sm">
                                      <p className="text-sm font-bold text-gray-700 leading-tight">
                                        * This answer was taken from "Used Resources"
                                      </p>
                                    </div>
                                  </div>
                                </>
                              );
                            }

                            if (!additional.length) return null;
                            return (
                              <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                  onClick={() => toggleAccordion(chat.id, 'additional')}
                                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-gray-700">Additional resources</span>
                                    <span className="text-xs text-gray-500">({additional.length})</span>
                                  </div>
                                  <svg
                                    className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded(chat.id, 'additional') ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                                {isExpanded(chat.id, 'additional') && (
                                  <div className="px-3 py-2 bg-white">
                                    <ol className="space-y-1">
                                      {additional?.map((src, idx) => {
                                        const s = src || {};
                                        const score = typeof s?.score === 'number' ? s.score : typeof s?.max_score === 'number' ? s.max_score : undefined;
                                        const scorePct = typeof score === 'number' ? `${(score * 100).toFixed(1)}%` : '';
                                        return (
                                          <li key={`${chat.id}-add-${idx}`} className="flex items-start text-xs text-gray-700 hover:underline cursor-pointer" onClick={() => onSourceClick(s)}>
                                            <span className="mr-2">{idx + 1}.</span>
                                            <span className="text-blue-600 hover:text-blue-700">
                                              {s?.file || 'Unknown'}
                                              {typeof s?.pages !== 'undefined' ? ` • page[${s.pages}]` : ''}
                                              {scorePct && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                                  {scorePct}
                                                </span>
                                              )}
                                            </span>
                                          </li>
                                        );
                                      })}
                                    </ol>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ) : null}
                      {chat.streaming && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                          <span className="dot" />
                          <span className="dot" />
                          <span className="dot" />
                          <span className="ml-2">Generating…</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-400 mb-2 font-medium">{timestamp}</span>
              </div>
            )}
          </div>
        );
      })}
      {isLoading && !chatHistory.some((m) => m.type === 'ai' && m.streaming) && (
        <div className="flex justify-start message-enter">
          <div className="thinking-box max-w-xs lg:max-w-md px-4 py-3 rounded-3xl rounded-bl-none">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#4F39F6]">{currentThinkingState}</span>
              <div className="flex gap-1">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
      <Dialog
        header="PDF Preview"
        visible={previewPdf.show}
        onHide={() => setPreviewPdf({ show: false, url: '' })}
        style={{ width: '80vw', maxWidth: '1000px' }}
        modal
        draggable={false}
        resizable={false}
        className="p-fluid"
      >
        <div className="w-full" style={{ height: '70vh' }}>
          <PdfPreview url={previewPdf.url} />
        </div>
      </Dialog>
    </div>
  );
}

export default React.memo(ChatHistory);